import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  MAX_FILE_SIZE,
  useUploadDocument,
  type DocumentRow,
} from '@/hooks/useDocuments';

export interface MessageAttachmentRow {
  id: string;
  clinic_id: string;
  message_id: string;
  document_id: string;
  created_by: string | null;
  created_at: string;
  document: DocumentRow;
}

export const MESSAGE_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
] as const;

export function validateMessageAttachmentFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) return 'size_limit_exceeded';
  if (!MESSAGE_ALLOWED_MIME_TYPES.includes(file.type as (typeof MESSAGE_ALLOWED_MIME_TYPES)[number])) {
    return 'invalid_type';
  }
  return null;
}

export const messageAttachmentsKey = (messageId: string | null | undefined) =>
  ['messages', 'attachments', messageId ?? null] as const;

export function useMessageAttachments(messageId: string | null | undefined) {
  return useQuery({
    queryKey: messageAttachmentsKey(messageId),
    enabled: Boolean(messageId),
    queryFn: async (): Promise<MessageAttachmentRow[]> => {
      if (!messageId) return [];
      const { data, error } = await supabase
        .from('message_attachments')
        .select(
          'id, clinic_id, message_id, document_id, created_by, created_at, document:documents(*)',
        )
        .eq('message_id', messageId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      return ((data ?? []) as unknown as Array<Omit<MessageAttachmentRow, 'document'> & { document: DocumentRow }>).map(
        (row) => ({
          id: row.id,
          clinic_id: row.clinic_id,
          message_id: row.message_id,
          document_id: row.document_id,
          created_by: row.created_by,
          created_at: row.created_at,
          document: row.document,
        }),
      );
    },
    staleTime: 10_000,
  });
}

export function useAttachMessageDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { message_id: string; document_id: string }) => {
      const { data, error } = await supabase.rpc('attach_message_document', {
        p_message_id: input.message_id,
        p_document_id: input.document_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: messageAttachmentsKey(input.message_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents', 'patient'] });
      }
    },
  });
}

export function useDetachMessageDocument(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { attachment_id: string; message_id: string }) => {
      const { data, error } = await supabase.rpc('detach_message_document', {
        p_attachment_id: input.attachment_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: messageAttachmentsKey(input.message_id) });
      if (clinicId) {
        qc.invalidateQueries({ queryKey: ['documents'] });
      }
    },
  });
}

export function useUploadMessageAttachment(clinicId: string | null | undefined) {
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(clinicId);

  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      messageId: string;
      file: File;
    }): Promise<DocumentRow> => {
      const doc = await uploadDocument.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        file: input.file,
        tags: ['message'],
      });
      await attachDocument.mutateAsync({
        message_id: input.messageId,
        document_id: doc.id,
      });
      return doc;
    },
  });
}
